import { useState, useCallback, useRef, useEffect } from 'react';
import { WorkerMessageType } from '@/worker/types';
import type { SectionPlane } from '@shared/types';
import i18n from '@/i18n/i18n';

export type ProjectionView = 'top' | 'front' | 'right' | 'isometric' | 'rear' | 'left' | 'bottom';

export type ProjectionState = {
  svgUrl: string | null;
  svgString: string | null;
  isCompiling: boolean;
  error: Error | null;
  /** If this projection is for a specific component, its module name */
  componentName?: string;
};

type SectionState = ProjectionState & {
  plane: SectionPlane;
};

const VIEWS: ProjectionView[] = ['top', 'front', 'right', 'isometric'];
const ALL_VIEWS: ProjectionView[] = ['top', 'front', 'right', 'isometric', 'rear', 'left', 'bottom'];

const initialProjections: Record<ProjectionView, ProjectionState> = {
  top: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  front: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  right: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  isometric: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  rear: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  left: { svgUrl: null, svgString: null, isCompiling: false, error: null },
  bottom: { svgUrl: null, svgString: null, isCompiling: false, error: null },
};

/**
 * Wraps OpenSCAD code in projection() for a given view.
 * If componentName is provided, projects only that module instead of the full assembly.
 * Extracts use/include directives to keep them at the top level
 * (they can't go inside modules), then wraps the rest in a module
 * so projection() can operate on the full geometry.
 */
function wrapWithProjection(code: string, view: ProjectionView, componentName?: string): string {
  const lines = code.split('\n');
  const topLevelDirectives: string[] = [];
  const bodyLines: string[] = [];

  for (const line of lines) {
    if (/^\s*(use|include)\s+</.test(line)) {
      topLevelDirectives.push(line);
    } else {
      bodyLines.push(line);
    }
  }

  const directives = topLevelDirectives.length > 0
    ? topLevelDirectives.join('\n') + '\n\n'
    : '';

  let rotation = '';
  switch (view) {
    case 'top':
      break;
    case 'front':
      rotation = ' rotate([90, 0, 0])';
      break;
    case 'right':
      rotation = ' rotate([90, 0, 90])';
      break;
    case 'isometric':
      rotation = ' rotate([35.264, 0, 45])';
      break;
    case 'rear':
      rotation = ' rotate([90, 0, 180])';
      break;
    case 'left':
      rotation = ' rotate([90, 0, -90])';
      break;
    case 'bottom':
      rotation = ' rotate([180, 0, 0])';
      break;
  }

  if (componentName) {
    // Component-level projection: include full code (for parameters/helpers)
    // but project only the specified module
    return `${directives}${bodyLines.join('\n')}\nprojection(cut = false)${rotation} ${componentName}();\n`;
  }

  return `${directives}module __proj_source__() {\n${bodyLines.join('\n')}\n}\nprojection(cut = false)${rotation} __proj_source__();\n`;
}

/**
 * Wraps OpenSCAD code with projection(cut=true) and translate for section views.
 */
function wrapWithSectionProjection(code: string, plane: SectionPlane): string {
  const lines = code.split('\n');
  const topLevelDirectives: string[] = [];
  const bodyLines: string[] = [];

  for (const line of lines) {
    if (/^\s*(use|include)\s+</.test(line)) {
      topLevelDirectives.push(line);
    } else {
      bodyLines.push(line);
    }
  }

  const directives = topLevelDirectives.length > 0
    ? topLevelDirectives.join('\n') + '\n\n'
    : '';

  // Build translate based on axis and position
  const translate = plane.axis === 'x'
    ? `translate([-${plane.position}, 0, 0])`
    : plane.axis === 'y'
      ? `translate([0, -${plane.position}, 0])`
      : `translate([0, 0, -${plane.position}])`;

  // Rotate to show the section from the correct angle
  const rotation = plane.axis === 'x'
    ? 'rotate([0, 0, 90])'
    : plane.axis === 'z'
      ? 'rotate([90, 0, 0])'
      : '';

  return `${directives}module __section_source__() {\n${bodyLines.join('\n')}\n}\nprojection(cut = true) ${rotation} ${translate} __section_source__();\n`;
}

export function useProjectionViews() {
  const [projections, setProjections] =
    useState<Record<ProjectionView, ProjectionState>>(initialProjections);
  const [sections, setSections] = useState<SectionState[]>([]);
  const [componentProjections, setComponentProjections] =
    useState<Record<string, ProjectionState>>({});
  const workerRef = useRef<Worker | null>(null);
  const codeRef = useRef<string | null>(null);
  const blobUrlsRef = useRef<Map<string, string>>(new Map());
  const pendingRequestsRef = useRef<
    Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void }>
  >(new Map());

  const handleMessage = useCallback((event: MessageEvent) => {
    const { id, err, data } = event.data;

    // Handle fs.write responses
    if (id && pendingRequestsRef.current.has(id)) {
      const pending = pendingRequestsRef.current.get(id)!;
      pendingRequestsRef.current.delete(id);
      if (err) {
        pending.reject(new Error(err.message || 'Worker operation failed'));
      } else {
        pending.resolve(data);
      }
      return;
    }

    // Handle component view responses (format: "component-{name}-{view}")
    if (id && typeof id === 'string' && id.startsWith('component-')) {
      const parts = (id as string).split('-');
      // component-{componentName}-{view}
      const componentName = parts.slice(1, -1).join('-');
      const viewName = parts[parts.length - 1];
      const compKey = `${componentName}_${viewName}`;

      if (err) {
        setComponentProjections((prev) => ({
          ...prev,
          [compKey]: {
            svgUrl: null, svgString: null, isCompiling: false,
            error: new Error(err.message || 'Component projection failed'),
            componentName,
          },
        }));
      } else if (data?.output) {
        const svgString = new TextDecoder().decode(data.output);
        const blob = new Blob([data.output], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const oldUrl = blobUrlsRef.current.get(compKey);
        if (oldUrl) URL.revokeObjectURL(oldUrl);
        blobUrlsRef.current.set(compKey, url);

        setComponentProjections((prev) => ({
          ...prev,
          [compKey]: { svgUrl: url, svgString, isCompiling: false, error: null, componentName },
        }));
      }
      return;
    }

    // Handle section view responses
    if (id && typeof id === 'string' && id.startsWith('section-')) {
      const sectionIdx = parseInt(id.replace('section-', ''), 10);
      if (isNaN(sectionIdx)) return;

      setSections((prev) => {
        const updated = [...prev];
        if (sectionIdx >= updated.length) return prev;
        if (err) {
          updated[sectionIdx] = {
            ...updated[sectionIdx],
            svgUrl: null,
            svgString: null,
            isCompiling: false,
            error: new Error(err.message || 'Section compilation failed'),
          };
        } else if (data?.output) {
          const svgString = new TextDecoder().decode(data.output);
          const blob = new Blob([data.output], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const oldUrl = blobUrlsRef.current.get(`section-${sectionIdx}`);
          if (oldUrl) URL.revokeObjectURL(oldUrl);
          blobUrlsRef.current.set(`section-${sectionIdx}`, url);
          updated[sectionIdx] = {
            ...updated[sectionIdx],
            svgUrl: url,
            svgString,
            isCompiling: false,
            error: null,
          };
        } else {
          updated[sectionIdx] = {
            ...updated[sectionIdx],
            svgUrl: null,
            svgString: null,
            isCompiling: false,
            error: new Error(i18n.t('services.noSectionOutput', { ns: 'common' })),
          };
        }
        return updated;
      });
      return;
    }

    // Handle projection responses
    if (!id || typeof id !== 'string' || !id.startsWith('projection-')) return;
    const view = id.replace('projection-', '') as ProjectionView;
    if (!ALL_VIEWS.includes(view)) return;

    if (err) {
      console.error(`[useProjectionViews] Projection "${view}" FAILED:`, err.message || err);
      setProjections((prev) => ({
        ...prev,
        [view]: {
          svgUrl: null,
          svgString: null,
          isCompiling: false,
          error: new Error(err.message || 'Projection compilation failed'),
        },
      }));
    } else if (data?.output) {
      // Revoke old URL
      const oldUrl = blobUrlsRef.current.get(view);
      if (oldUrl) URL.revokeObjectURL(oldUrl);

      // Decode raw SVG string from Uint8Array
      const svgString = new TextDecoder().decode(data.output);

      const blob = new Blob([data.output], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      blobUrlsRef.current.set(view, url);

      setProjections((prev) => ({
        ...prev,
        [view]: { svgUrl: url, svgString, isCompiling: false, error: null },
      }));
    } else {
      // No error and no output — treat as failure
      console.warn(`[useProjectionViews] Projection "${view}" returned NO output`);
      setProjections((prev) => ({
        ...prev,
        [view]: {
          svgUrl: null,
          svgString: null,
          isCompiling: false,
          error: new Error(i18n.t('services.noProjectionOutput', { ns: 'common' })),
        },
      }));
    }
  }, []);

  const getWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker(
        new URL('../worker/worker.ts', import.meta.url),
        { type: 'module' },
      );
      workerRef.current.addEventListener('message', handleMessage);
    }
    return workerRef.current;
  }, [handleMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrlsRef.current.clear();
      pendingRequestsRef.current.forEach((pending) => {
        pending.reject(new Error('Worker terminated'));
      });
      pendingRequestsRef.current.clear();
    };
  }, []);

  const writeFile = useCallback(
    async (path: string, content: Blob | File): Promise<void> => {
      const worker = getWorker();
      const arrayBuffer = await content.arrayBuffer();
      const requestId = `fs-write-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const responsePromise = new Promise<void>((resolve, reject) => {
        pendingRequestsRef.current.set(requestId, {
          resolve: () => resolve(),
          reject,
        });
      });

      worker.postMessage(
        {
          id: requestId,
          type: WorkerMessageType.FS_WRITE,
          data: {
            path,
            content: arrayBuffer,
            type: content.type,
          },
        },
        [arrayBuffer],
      );

      await responsePromise;
    },
    [getWorker],
  );

  const compileProjections = useCallback(
    (scadCode: string, views?: ProjectionView[]) => {
      // Skip if same code already compiled
      if (codeRef.current === scadCode) {
        return;
      }
      codeRef.current = scadCode;

      const worker = getWorker();
      const viewsToCompile = views ?? VIEWS;

      // Reset views to compiling
      const resetState: Record<string, ProjectionState> = {};
      for (const v of ALL_VIEWS) {
        resetState[v] = viewsToCompile.includes(v)
          ? { svgUrl: null, svgString: null, isCompiling: true, error: null }
          : projections[v] ?? { svgUrl: null, svgString: null, isCompiling: false, error: null };
      }
      setProjections(resetState as Record<ProjectionView, ProjectionState>);

      for (const view of viewsToCompile) {
        const wrappedCode = wrapWithProjection(scadCode, view);
        worker.postMessage({
          id: `projection-${view}`,
          type: WorkerMessageType.PREVIEW_SVG,
          data: { code: wrappedCode, params: [], fileType: 'svg' },
        });
      }
    },
    [getWorker, projections],
  );

  const compileSections = useCallback(
    (scadCode: string, planes: SectionPlane[]) => {
      if (planes.length === 0) return;
      const worker = getWorker();

      // Initialize section states
      setSections(
        planes.map((plane) => ({
          plane,
          svgUrl: null,
          svgString: null,
          isCompiling: true,
          error: null,
        })),
      );

      // Send compilation for each section
      for (let i = 0; i < planes.length; i++) {
        const wrappedCode = wrapWithSectionProjection(scadCode, planes[i]);
        worker.postMessage({
          id: `section-${i}`,
          type: WorkerMessageType.PREVIEW_SVG,
          data: { code: wrappedCode, params: [], fileType: 'svg' },
        });
      }
    },
    [getWorker],
  );

  /**
   * Compile per-component projection views.
   * Each component module is projected individually for detailed part drawings.
   */
  const compileComponentViews = useCallback(
    (scadCode: string, components: Array<{ componentId: string; views: ProjectionView[] }>) => {
      const worker = getWorker();

      // Reset component projections to compiling state
      const resetState: Record<string, ProjectionState> = {};
      for (const comp of components) {
        for (const view of comp.views) {
          const key = `${comp.componentId}_${view}`;
          resetState[key] = {
            svgUrl: null, svgString: null, isCompiling: true, error: null,
            componentName: comp.componentId,
          };
        }
      }
      setComponentProjections(resetState);

      // Send compilation requests for each component-view pair
      for (const comp of components) {
        for (const view of comp.views) {
          const wrappedCode = wrapWithProjection(scadCode, view, comp.componentId);
          worker.postMessage({
            id: `component-${comp.componentId}-${view}`,
            type: WorkerMessageType.PREVIEW_SVG,
            data: { code: wrappedCode, params: [], fileType: 'svg' },
          });
        }
      }
    },
    [getWorker],
  );

  const invalidateCache = useCallback(() => {
    codeRef.current = null;
  }, []);

  const isAnyCompiling = Object.values(projections).some((p) => p.isCompiling) ||
    sections.some((s) => s.isCompiling) ||
    Object.values(componentProjections).some((p) => p.isCompiling);

  return {
    projections,
    sections,
    componentProjections,
    compileProjections,
    compileSections,
    compileComponentViews,
    isAnyCompiling,
    writeFile,
    invalidateCache,
  };
}
